import type { Dispatch } from "redux";

import { BettingState } from "@ppb/betslip-core";
import { FETCH_CARDS, FetchCardsAction } from "@ppb/tbd-store";
import {
  BETTING__SBK_ACCA_INSURANCE_TOGGLE,
  BETTING__SBK_GHOST_LEG_TOGGLE,
  BETTING__SBK_MONEY_BACK_TOGGLE,
  BETTING__SBK_PRICE_BOOST_TOGGLE,
  BETTING__SPORTSBOOK_GENEROSITY_WALLET_BET_ACTION,
  BETTING__SPORTSBOOK_REMOVE_ALL_COMBINATION_FREE_BETS_WALLETS_ACTION,
  BettingSportsbookGenerosityWalletBetAction,
  BettingSportsbookMoneyBackToggleAction,
  BettingSportsbookRemoveAllCombinationWalletsAction,
  BettingSportsbookRemoveGenerosityWalletsAction,
  UI__GENEROSITY_WALLET_REMOVE_CLICK,
  type BettingSportsbookAccaInsuranceToggleAction,
  type BettingSportsbookGhostLegToggleAction,
  type BettingSportsbookPriceBoostToggleAction,
} from "@ppb/tbd-store/actions/betting";
import { getSportsbookBettingState } from "@ppb/tbd-store/state/betting/sportsbook-betting/sportsbook-betting-selectors";
import { createGetCombinationsSelectedWalletsAmounts } from "../store/state/sportsbook-betting/sportsbook-betting-selectors";

import { GenerosityIconName } from "@ppb/the-wall-common/types";
import { codecs } from "@ppb/tbd-urn-codecs";

import { BetslipLocalization, BetslipOddsDisplay } from "../contract";
import { getGenerosityBetslipAlertData, GenerosityWalletType } from "../helpers/generosity-wallets";
import type { BetslipApplicationState } from "../store/state/BetslipApplicationState";

const EXTRA_WALLET_CARD_GROUP_URN = codecs.cardGroup.extraWallet.encode().uid;

export type GenerosityStateProps = {
  hasGenerosity: boolean;
  isGenerosityWalletSelected: boolean;
  generosityAlertMessage?: string;
  generosityIconName?: GenerosityIconName;
  generosityType?: GenerosityWalletType;
};

type GenerosityPropsParams = {
  combination: BettingState.Combination;
  combinationId: string;
  isSingleBetBetslip: boolean;
  localization: BetslipLocalization;
  oddsDisplayPreference: BetslipOddsDisplay;
};

export const createGenerosityPropsSelector = () => {
  const getCombinationsSelectedWalletsAmounts = createGetCombinationsSelectedWalletsAmounts();

  return (
    state: BetslipApplicationState,
    { combination, combinationId, isSingleBetBetslip, localization, oddsDisplayPreference }: GenerosityPropsParams,
  ): GenerosityStateProps => {
    const { wallets } = getSportsbookBettingState(state);

    const isFreeBetsSelected =
      !!wallets && !!Object.values(wallets).find(({ combinationId: combId }) => combId === combinationId);

    const isAccaInsuranceTokenSelected = !!combination.accaInsuranceTokenId;
    const isPriceBoostSelected = !!combination.isPriceBoostSelected;
    const isMoneyBackTokenSelected = !!combination.isMoneyBackSelected;
    const isGhostLegTokenSelected = !!combination.isGhostLegSelected;

    const selectedGhostLegOffer = combination.ghostLegTokenId
      ? combination.ghostLegOffers.find(({ tokenId }) => tokenId === combination.ghostLegTokenId)
      : undefined;
    const ghostLegs = selectedGhostLegOffer?.numberOfLegs;
    const ghostLegMaxStake = selectedGhostLegOffer?.maxStake ?? undefined;

    let combinationsSelectedWalletsAmount;

    if (isFreeBetsSelected && !isSingleBetBetslip) {
      combinationsSelectedWalletsAmount = getCombinationsSelectedWalletsAmounts(state.betting?.sportsbookBetting)[
        combinationId
      ];
    }

    const { numLines = 0, combinationAmountPerLine, combinationAmount } = combinationsSelectedWalletsAmount ?? {};

    const {
      message: generosityAlertMessage,
      icon: generosityIconName,
      type: generosityType,
    } = getGenerosityBetslipAlertData({
      combination,
      numLines,
      combinationAmountPerLine,
      combinationAmount,
      isAccaInsuranceTokenSelected,
      isGhostLegTokenSelected,
      isFreeBetsSelected,
      isPriceBoostSelected,
      isMoneyBackTokenSelected,
      isSingleBetBetslip,
      localization,
      ghostLegs,
      ghostLegMaxStake,
      oddsDisplayPreference,
    });

    const hasGenerosity =
      !!combination.applicableWallets?.length ||
      !!combination.priceBoostOffers?.length ||
      !!combination.accaInsuranceOffers?.length ||
      !!combination.moneyBackOffers?.length ||
      (!combination.isInplay && !!combination.ghostLegOffers?.length);

    return {
      hasGenerosity,
      isGenerosityWalletSelected:
        isFreeBetsSelected ||
        isPriceBoostSelected ||
        !!combination.accaInsuranceTokenId ||
        !!combination.moneyBackTokenId ||
        !!combination.ghostLegTokenId,
      generosityAlertMessage,
      generosityIconName,
      generosityType,
    };
  };
};

export const dispatchGenerosityPress = (dispatch: Dispatch, combinationIds: string[], isSelected: boolean) => {
  combinationIds.forEach((combinationId) => {
    dispatch<BettingSportsbookGenerosityWalletBetAction>({
      type: BETTING__SPORTSBOOK_GENEROSITY_WALLET_BET_ACTION,
      payload: {
        combinationId,
        isSelected,
      },
    });
  });

  dispatch<FetchCardsAction>({
    type: FETCH_CARDS,
    payload: {
      urns: [EXTRA_WALLET_CARD_GROUP_URN],
    },
  });
};

export const dispatchGenerosityWalletRemoveAction = (
  dispatch: Dispatch,
  combinationIds: string[],
  type?: GenerosityWalletType,
) => {
  combinationIds.forEach((combinationId) => {
    if (type === "BONUS_CASH") {
      dispatch<BettingSportsbookRemoveAllCombinationWalletsAction>({
        type: BETTING__SPORTSBOOK_REMOVE_ALL_COMBINATION_FREE_BETS_WALLETS_ACTION,
        payload: { combinationId },
      });
      return;
    }

    if (type === "ACCA_INSURANCE_TOKEN") {
      dispatch<BettingSportsbookAccaInsuranceToggleAction>({
        type: BETTING__SBK_ACCA_INSURANCE_TOGGLE,
        payload: { combinationId },
      });
      return;
    }

    if (type === "PRICE_BOOST_TOKEN") {
      dispatch<BettingSportsbookPriceBoostToggleAction>({
        type: BETTING__SBK_PRICE_BOOST_TOGGLE,
        payload: { combinationId },
      });
      return;
    }

    if (type === "MONEY_BACK_TOKEN") {
      dispatch<BettingSportsbookMoneyBackToggleAction>({
        type: BETTING__SBK_MONEY_BACK_TOGGLE,
        payload: { combinationId },
      });
      return;
    }

    if (type === "GHOST_LEG_TOKEN") {
      dispatch<BettingSportsbookGhostLegToggleAction>({
        type: BETTING__SBK_GHOST_LEG_TOGGLE,
        payload: { combinationId },
      });
    }
  });
};

export const dispatchGenerosityRemoveAction = (dispatch: Dispatch) => {
  dispatch<BettingSportsbookRemoveGenerosityWalletsAction>({
    type: UI__GENEROSITY_WALLET_REMOVE_CLICK,
  });
};
